// W42 W4 · **W43 `ADR-0109`** — the card roles.
//
// A hero card is the ONE pale card at the top of a screen, carrying the mascot
// and the primary action. An accent card is a saturated `surface-*` block, at
// most `MAX_ACCENT_CARDS` per screen. Everything else is white.
//
// This is a component rather than four hand-written `bg-surface-*` blocks
// because two of its rules are rules about **pairs**: a mascot's colour must
// never equal the colour of the card it sits on, and `coral` is only legal on a
// card carrying a bare numeral. Four hand-written blocks is four chances to get
// both wrong, and the first failure is invisible — a character drawn in its own
// card's colour is simply not there.
//
// The allocation itself lives in `lib/screenType.ts` (`HERO_SLOTS` /
// `ACCENT_SLOTS`), so the table that decides it is reviewable next to the route
// classification rather than scattered through four components.

import type { ReactNode } from "react";

import { Mascot } from "@/components/ui/Mascot";
import {
  assertCoralRole,
  assertMascotContrast,
  type AccentSlot,
  type HeroSlot,
} from "@/lib/screenType";
import { cn } from "@/lib/utils/cn";

/** Which tier this card paints itself. */
export type CardRole =
  /** The one pale mascot card, top of a hero screen. */
  | "hero"
  /** A saturated accent card. */
  | "accent"
  /** White — the default for every secondary card, row and large card. */
  | "plain"
  /** Translucent white + blur, the floating layer. */
  | "glass";

const ROLE_CLASS: Record<CardRole, string> = {
  hero: "surface-hero",
  accent: "", // the fill comes from the slot's own `surface-*`
  plain: "surface-plain",
  glass: "surface-glass",
};

export interface HeroCardProps {
  /** Which hero this is. Carries both the pale fill and the mascot. */
  slot: HeroSlot;
  /** Element to render. `div` by default; `article` for a diary record. */
  as?: "div" | "article";
  /** `data-testid`, so a test can assert "exactly one hero card on this screen". */
  testId?: string;
  /**
   * Accessible name, which also promotes the card to `role="region"`.
   *
   * Not cosmetic: `HeroSection.test.tsx` finds the anniversaries hero by
   * `role="region"` + its name, and that landmark is how a screen-reader user
   * jumps past the shell. Dropping the `div` for a plain container took the
   * landmark with it and the test caught it.
   */
  label?: string;
  /** Id of the element this card describes, if any. */
  describedBy?: string;
  /**
   * `data-anchor="true"` — the pair's founding record, or a private entry.
   *
   * Carried because the other two heroes (`AnniversaryHero`,
   * `TimelineEventCard`) already mark it, and the marker is what a future
   * stylesheet or test keys on.
   */
  dataAnchor?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * The screen's hero: pale, one mascot, the primary action.
 *
 * `relative` is load-bearing and is why the mascot cannot escape. The mascot is
 * `position: absolute`; with no positioned ancestor it resolves against the
 * **page** — which is exactly what happened once, when `DiaryHero` was a
 * hand-rolled `<article>` that took the fill and the mascot but not `relative`,
 * and the lime character landed on the topbar avatar. `w42-screen-type.test.ts`
 * now fails if any other file declares a mascot, so there is no second place to
 * forget it.
 */
export function HeroCard({
  slot,
  as: Tag = "div",
  testId,
  dataAnchor,
  label,
  describedBy,
  className,
  children,
}: HeroCardProps): JSX.Element {
  // F17, still live. The hero's own fill is passed rather than a placeholder —
  // `HeroCard` originally passed the literal `"surface-aqua"`, which made the
  // anniversaries hero (whose mascot *is* aqua) throw on correct input. A check
  // that fires on correct input is worse than no check.
  assertMascotContrast(slot.tint, slot.mascot);
  return (
    <Tag
      data-component="HeroCard"
      // A stable hook independent of `data-component`, so "how many hero cards
      // are on this screen" is answerable without knowing which component
      // happens to be the hero on this route. `w42-route-audit.mjs` uses it.
      data-hero-card={slot.tint}
      data-testid={testId}
      data-anchor={dataAnchor ? "true" : undefined}
      data-surface={slot.tint}
      data-mascot={slot.mascot}
      role={label ? "region" : undefined}
      aria-label={label}
      aria-describedby={describedBy}
      className={cn("relative", ROLE_CLASS.hero, "rounded-lg p-s-5 shadow-hero", className)}
    >
      {/* `aria-hidden` on the wrapper, not on the svg: `Mascot` already carries a
          `role="img"` + `aria-label`, and the pair is decorative here because the
          card's own text says what the screen is about. Two announcements of the
          same sentiment is worse than none. */}
      <span aria-hidden className="hero-mascot">
        <Mascot name={slot.mascot} size={56} />
      </span>
      {children}
    </Tag>
  );
}

export interface AccentCardProps {
  /** Which accent this is. The fill comes from the slot, not from a prop. */
  slot: AccentSlot;
  as?: "div" | "article" | "section";
  testId?: string;
  label?: string;
  className?: string;
  children: ReactNode;
}

/**
 * A saturated accent card.
 *
 * No mascot: the reference puts exactly one character on the screen, on the
 * hero. An accent is colour and nothing else, which is what keeps the hero
 * reading as the hero.
 */
export function AccentCard({
  slot,
  as: Tag = "div",
  testId,
  label,
  className,
  children,
}: AccentCardProps): JSX.Element {
  assertCoralRole(slot);
  return (
    <Tag
      data-component="AccentCard"
      data-accent-card={slot.surface}
      data-testid={testId}
      data-surface={slot.surface}
      data-role={slot.role}
      role={label ? "region" : undefined}
      aria-label={label}
      className={cn("relative", slot.surface, "rounded-lg p-s-5 shadow-hero", className)}
    >
      {children}
    </Tag>
  );
}

/**
 * W53.2 `ADR-0183` — `PlainCard` and `export { ROLE_CLASS }` are **deleted**.
 *
 * `PlainCard` had zero non-test users, and this repository has already been
 * burned twice by a zero-reference export carrying a comment that asserts it is
 * the right answer: `BARE_CARD` below, and `.nav-chrome` in `DESIGN.md`. Both
 * times the consequence was the same — an audit read the comment, found no
 * users, and reported the defect as fixed while the screenshot said otherwise.
 * A card component nobody renders is the next one of those.
 *
 * It was also **not the contract it claimed to be**: `PlainCard` painted
 * `surface-plain rounded-2xl shadow-card` with **no border**, while `Card`'s
 * default tier paints `surface-plain rounded-2xl border-hairline shadow-card`.
 * Two components with the same name for 「那張白卡」, differing on the border —
 * which is D-2 wearing a component's clothes.
 *
 * `ROLE_CLASS` stays, unexported, because `HeroCard` above reads
 * `ROLE_CLASS.hero` from it. The *export* went: nothing outside this file ever
 * imported it, and an unimported export is indistinguishable from a public API.
 */

/**
 * W45.0 `ADR-0113` — this file used to end with
 *
 *     export const BARE_CARD = "bg-transparent border-0 shadow-none";
 *
 * and a comment describing it as the working way to drop a nested card's
 * surface. **It was zero-reference across `src`, and the approach it represents
 * had already failed**: `DECISIONS.md`'s `ADR-0109` records that the first cut
 * was exactly this class and it did not take effect, because `.surface-plain` is
 * an unlayered rule and Tailwind utilities live in `@layer utilities`, so the
 * lower-specificity one loses. The real mechanism is `Card`'s `bare` prop, which
 * skips the surface at the source.
 *
 * Deleted rather than left in place: a zero-reference constant carrying a comment
 * that asserts it is the solution is what produced the misreport — an audit read
 * the comment, found the string had no users, and reported the dashboard card as
 * clean while the screenshot showed the defect.
 *
 * The contract it was gesturing at now lives in one place: `Card`'s `bare` means
 * **no surface at all** — fill, border and shadow.
 */
